import { useQueryClient } from '@tanstack/react-query'
import { SaveIcon, UserPlusIcon } from 'lucide-react'
import React, { useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'

import { type MetadataConfiguration } from 'components/quoting/types'
import { adminProspects, partnersProspects } from 'rails_routes'
import { usePostQuotingProspects, usePutQuotingProspectsId } from 'utils/web_api'
import { type PutQuotingProspectsId, type QuotingProspect } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import { FormTextFieldDS } from '../reusable/form/FormFields'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../ui/sheet'

const formDataFromPutQuotingProspectsId = (prospect: PutQuotingProspectsId) => {
  const fd = new FormData()
  if (prospect.name) { fd.append('name', prospect.name) }
  if (prospect.email) { fd.append('email', prospect.email) }
  if (prospect.external_link) { fd.append('external_link', prospect.external_link) }
  if (prospect.ein) { fd.append('ein', prospect.ein) }
  if (prospect.partner_metadata) {
    Object.entries(prospect.partner_metadata).forEach(([k, v]) => {
      if (v !== undefined && v !== null) {
        fd.append(`partner_metadata[${k}]`, String(v))
      }
    })
  }
  return fd
}

export function CreateOrEditProspect ({
  prospect,
  isCreate,
  isForAdminView = false,
  triggerChildren,
  showEIN: collectEIN,
  metadataConfiguration,
}: {
  prospect?: QuotingProspect;
  isCreate: boolean;
  isForAdminView?: boolean;
  triggerChildren: React.ReactNode;
  showEIN?: boolean;
  metadataConfiguration?: MetadataConfiguration;
}) {
  const { assign } = useWindowLocation()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<QuotingProspect>({
    defaultValues: {
      name: prospect?.name || '',
      email: prospect?.email || '',
      external_link: isForAdminView ? prospect?.external_link : null,
      ein: prospect?.ein || '',
      partner_metadata: prospect?.partner_metadata || {},
    },
  })

  const [submissionError, setSubmissionError] = useState<string | undefined>()
  const queryClient = useQueryClient()
  const bodyRef = React.useRef<FormData | null>(null)

  const createMutation = usePostQuotingProspects({
    mutation: {
      onSuccess: () => {
        assign(isForAdminView ? adminProspects.index() : partnersProspects.index())
      },
      onError: (error: any) => {
        console.error('Error creating prospect:', error)
        setSubmissionError(
          'There was an error creating this prospect. Please contact support.'
        )
      },
    },
    request: {
      get body () {
        return bodyRef.current
      },
    },
  }, queryClient)

  const updateMutation = usePutQuotingProspectsId({
    mutation: {
      onSuccess: () => {
        assign(isForAdminView ? adminProspects.index() : partnersProspects.index())
      },
      onError: (error: any) => {
        console.error(`Error updating prospect ${prospect?.id}:`, error)
        setSubmissionError(
          'There was an error updating this prospect. Please contact support.'
        )
      }
    },
  }, queryClient)

  const submitting = createMutation.isPending || updateMutation.isPending

  const onSubmit: SubmitHandler<QuotingProspect> = async (data) => {
    setSubmissionError(undefined)

    const requestData: PutQuotingProspectsId = {
      name: data.name,
      email: data.email,
      ...(isForAdminView && data.external_link && { external_link: data.external_link }),
      ...(metadataConfiguration && data.partner_metadata && { partner_metadata: data.partner_metadata }),
      ...(collectEIN && data.ein && { ein: data.ein }),
    }

    if (isCreate) {
      bodyRef.current = formDataFromPutQuotingProspectsId(requestData)
      createMutation.mutate()
    } else {
      if (!prospect?.id) {
        setSubmissionError('Prospect ID is required for update')
        return
      }
      updateMutation.mutate({ id: prospect.id, data: requestData })
    }
  }
  return (
    <Sheet>
      <SheetTrigger asChild>
        {triggerChildren}
      </SheetTrigger>
      <SheetContent aria-describedby={undefined}>
        <SheetHeader>
          <SheetTitle className='pb-10'>
            {isCreate ? 'Create Prospect' : 'Edit Prospect'}
          </SheetTitle>
        </SheetHeader>
        {submissionError && (
          <div
            className='bg-red-100 border border-red-400 text-red-700 p-2 my-2 rounded relative text-center text-xs'
            role='alert'
          >
            {submissionError}
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='flex flex-col pt-6 pb-10 gap-y-4'>
            <FormTextFieldDS<QuotingProspect>
              disabled={submitting}
              error={errors.name}
              fieldId='name'
              fieldLabel='Prospect name'
              register={register}
              validationRule={{ required: 'Name is required' }}
            />
            <FormTextFieldDS<QuotingProspect>
              disabled={submitting}
              error={errors.email}
              fieldId='email'
              fieldLabel='Prospect email'
              register={register}
            />
            {collectEIN && (
              <FormTextFieldDS<QuotingProspect>
                disabled={submitting}
                error={errors.ein}
                fieldId='ein'
                fieldLabel='Prospect EIN (optional)'
                register={register}
              />
            )}
            {isForAdminView && (
              <FormTextFieldDS<QuotingProspect>
                disabled={submitting}
                error={errors.external_link}
                fieldId='external_link'
                fieldLabel='Salesforce opportunity link (optional)'
                register={register}
              />
            )}
            {
              metadataConfiguration && (
                <div className='pt-4'>
                  <div className='pb-6'>
                    <p className='text-md text-gray-700 font-semibold'>{metadataConfiguration.title}</p>
                    {metadataConfiguration.description && (
                      <p className='text-sm text-gray-500'>{metadataConfiguration.description}</p>
                    )}
                  </div>
                  <div className='flex flex-col gap-y-4'>
                    {metadataConfiguration.fields.map((field) => (
                      <FormTextFieldDS<QuotingProspect>
                        key={field}
                        fieldId={`partner_metadata.${field}`}
                        fieldLabel={field}
                        register={register}
                        disabled={submitting}
                      />
                    ))}
                  </div>
                </div>
              )
            }
          </div>
          <footer>
            <button
              type='submit'
              className='btn-primary w-auto'
              disabled={submitting}
            >
              {isCreate
                ? (
                  <UserPlusIcon size={21} className='mr-2' />
                  )
                : (
                  <SaveIcon size={21} className='mr-2' />
                  )}
              {isCreate ? 'Create prospect' : 'Save changes'}
            </button>
          </footer>
        </form>
      </SheetContent>
    </Sheet>
  )
}
